import PropTypes from "prop-types";
import { Co2Equivalences } from "@signolteam/shared/co2-equivalences";
import AppUtils from "@src/utils/appUtils";
import { ImageWrapper } from "@src/components/atoms";
import FlippableCard from "./FlippableCard";
import VerticalLayout from "./VerticalLayout";
import SavingsCard from "./SavingsCard";
import HorizontalLayout from "./HorizontalLayout";
import CardContainer from "./CardContainer";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";
import { trackActionActivity } from "../../utils/userActivityTracker";
import HorizontalResponsiveLayout from "./HorizontalResponsiveLayout";

const EquivalenceCard = ({ layout, equivalence }) => {
  const isHorizontal = layout === "horizontal";

  const content = [
    <ImageWrapper src={equivalence?.iconUrl} size="sm" />,
    <p
      className={`h-full overflow-y-auto text-center ${isHorizontal ? "mr-8" : ""}`}
    >
      {equivalence?.equivalenceText}
    </p>,
  ];

  return (
    <CardContainer bgColor="transparent" shadow={false} padding={4}>
      {isHorizontal ? (
        <HorizontalResponsiveLayout
          vertical={{ layoutType: "flex-bottom" }}
          horizontal={{ ratio: ["flex-1"] }}
          gap={2}
        >
          {content}
        </HorizontalResponsiveLayout>
      ) : (
        <VerticalLayout layoutType="flex-bottom">{content}</VerticalLayout>
      )}
    </CardContainer>
  );
};

/**
 * Display CO₂ savings with a matching CO₂ equivalence on the back.
 * (A FlippableCard with SavingsCard for the front, and an equivalence on the back)
 */
const SavingsEquivalenceFlippableCard = ({
  industry,
  kgCo2,
  subject = "global",
  layout = "vertical",
  customerCode = null,
  overrideEquivalence = null,
  trackingActionKey = undefined,
}) => {
  const equivalence =
    overrideEquivalence ||
    new Co2Equivalences().computeOne(industry, kgCo2, subject, customerCode);

  const front = (
    <SavingsCard mode="co2" value={kgCo2} industry={industry} layout={layout} />
  );
  if (!equivalence) {
    return front;
  }

  return (
    <FlippableCard
      front={front}
      back={EquivalenceCard({ equivalence, layout })}
      onFlipStart={(flipped) => {
        if (trackingActionKey != null)
          trackActionActivity(trackingActionKey, { flipped });
      }}
    />
  );
};

export const savingsEquivalenceFlippableCardControls = {
  industry: COMMON_CONTROLS.industry,
  kgCo2: {
    propType: PropTypes.number.isRequired,
    description: "Input Value (CO₂ kg)",
    controlType: "number",
  },
  subject: {
    propType: PropTypes.oneOf(["operator", "global"]),
    description: "Show equivalence as personal (operator) or neutral (global)",
    controlType: "select",
    options: ["operator", "global"],
    defaultValue: "global",
  },
  layout: {
    propType: PropTypes.oneOf(["vertical", "horizontal"]),
    description: "display vertically or horizontally",
    controlType: "select",
    options: ["vertical", "horizontal"],
    defaultValue: "vertical",
  },
  overrideEquivalence: {
    propType: PropTypes.object,
    description: "Use a specific calculated co2 equivalence",
    disable: true,
  },
};

SavingsEquivalenceFlippableCard.propTypes = AppUtils.getPropTypeFromControls(
  savingsEquivalenceFlippableCardControls,
);

export default SavingsEquivalenceFlippableCard;
